/** Shapes from the booking service's `v2/admin/vouchers` endpoints. */

/** `VoucherValidityUnit` on the API. */
export const VALIDITY_UNIT = {
  days: 0,
  months: 1,
} as const;

export type ValidityUnit = (typeof VALIDITY_UNIT)[keyof typeof VALIDITY_UNIT];

export interface VoucherRestrictionTarget {
  id: string;
  nameEn: string | null;
  nameEs: string | null;
}

/**
 * What a voucher can be used on. An empty list means "any"; when several are
 * filled an event has to match one entry in each.
 */
export interface VoucherRestrictions {
  locations: VoucherRestrictionTarget[];
  serviceTypes: VoucherRestrictionTarget[];
  /** `EventType` values: 0 Rental, 1 Class, 2 Other, 3 Yoga, 4 Service. */
  eventTypes: number[];
  events: VoucherRestrictionTarget[];
  isUnrestricted: boolean;
}

/** A voucher in the catalogue, as `GET v2/admin/vouchers/products` returns it. */
export interface VoucherProduct {
  id: string;
  nameEn: string;
  nameEs: string;
  descriptionEn: string | null;
  descriptionEs: string | null;
  price: number;
  /** Free bookings per voucher. */
  uses: number;
  validityAmount: number;
  validityUnit: ValidityUnit;
  restrictions: VoucherRestrictions;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  /** Sold or issued from this product (paid ones only). */
  vouchersIssued: number;
}

/** `SaveVoucherProductModel` — the body of both create and edit. */
export interface SaveVoucherProduct {
  nameEn: string;
  nameEs: string;
  descriptionEn: string | null;
  descriptionEs: string | null;
  price: number;
  uses: number;
  validityAmount: number;
  validityUnit: ValidityUnit;
  sortOrder: number;
  isActive: boolean;
  restrictions: {
    locationIds: string[];
    serviceTypeIds: string[];
    eventTypes: number[];
    eventIds: string[];
  };
}

// ------------------------------------------------------------- management

/** `VoucherStatus` — the stored lifecycle. Claimed/used/expired are derived. */
export const VOUCHER_STATUS = {
  pendingPayment: 0,
  active: 1,
  paymentFailed: 2,
  cancelled: 3,
  voided: 4,
  refunded: 5,
} as const;

/** `VoucherBuyerStatus` — what the person who sent it sees. */
export const BUYER_STATUS = {
  unclaimed: 0,
  claimed: 1,
  expired: 2,
  refunded: 3,
} as const;

/** `VoucherHolderStatus` — what the person holding it sees. */
export const HOLDER_STATUS = {
  claimed: 0,
  used: 1,
  expired: 2,
} as const;

/** `VoucherAssignmentChangeKind`. */
export const CHANGE_KIND = {
  claimed: 0,
  reassigned: 1,
  emailChanged: 2,
} as const;

/** `VoucherClaimMethod`. */
export const CLAIM_METHOD = {
  self: 0,
  autoByEmail: 1,
  code: 2,
  admin: 3,
} as const;

export interface VoucherPerson {
  id: string;
  name: string;
  email: string | null;
}

/** One row of the management grid — `AdminVoucherDto`. */
export interface AdminVoucher {
  id: string;
  code: string;
  nameEn: string;
  nameEs: string;
  voucherProductId: string | null;
  isCustom: boolean;
  status: number;
  buyerStatus: number | null;
  holderStatus: number | null;
  isComplimentary: boolean;
  isExpired: boolean;
  price: number;
  amountPaid: number;
  amountRefunded: number;
  usesTotal: number;
  usesUsed: number;
  usesRemaining: number;
  expiresAt: string | null;
  createdAt: string;
  sentAt: string | null;
  claimedAt: string | null;
  buyer: VoucherPerson | null;
  holder: VoucherPerson | null;
  issuedBy: VoucherPerson | null;
  recipientName: string | null;
  recipientEmail: string | null;
  /** 0 English, 1 Spanish. */
  recipientLanguage: number;
  message: string | null;
  statusReason: string | null;
  adminNotes: string | null;
  restrictions: VoucherRestrictions;
}

export interface PagedAdminVouchers {
  vouchers: AdminVoucher[];
  total: number;
  page: number;
  pageSize: number;
}

export interface VoucherPayment {
  id: string;
  createdAt: string;
  amount: number;
  /** 0 Failed, 1 Success, 2 Pending. */
  status: number;
  method: number;
  isOffline: boolean;
  offlineReference: string | null;
  paypalReferenceNumber: string | null;
  refundedAmount: number;
  refundedAt: string | null;
  voidedAt: string | null;
}

export interface VoucherRedemption {
  id: string;
  eventBookingId: string;
  eventNameEn: string | null;
  eventNameEs: string | null;
  startTime: string;
  value: number;
  bookedFor: VoucherPerson | null;
  redeemedBy: VoucherPerson | null;
  createdAt: string;
  /** Set when the use was given back. */
  returnedAt: string | null;
}

export interface VoucherAssignmentChange {
  kind: number;
  claimMethod: number | null;
  from: VoucherPerson | null;
  to: VoucherPerson | null;
  fromEmail: string | null;
  toEmail: string | null;
  changedAt: string;
  changedBy: VoucherPerson | null;
  reason: string | null;
}

export interface AdminVoucherDetail extends AdminVoucher {
  payments: VoucherPayment[];
  redemptions: VoucherRedemption[];
  history: VoucherAssignmentChange[];
  suggestedRefund: number;
}

export interface VoucherSearchModel {
  searchTerm: string | null;
  status: number | null;
  claimed: boolean | null;
  complimentary: boolean | null;
  voucherProductId: string | null;
  page: number;
  pageSize: number;
}

export interface RestrictionsInput {
  locationIds: string[];
  serviceTypeIds: string[];
  eventTypes: number[];
  eventIds: string[];
}

/** `IssueVoucherModel`. */
export interface IssueVoucher {
  voucherProductId: string | null;
  nameEn?: string;
  nameEs?: string;
  descriptionEn?: string | null;
  descriptionEs?: string | null;
  uses?: number;
  validityAmount?: number;
  validityUnit?: ValidityUnit;
  expiresAt?: string | null;
  noExpiry?: boolean;
  restrictions?: RestrictionsInput;
  recipientUserId: string | null;
  recipientName: string | null;
  recipientEmail: string | null;
  recipientLanguage: number;
  message: string | null;
  isComplimentary: boolean;
  buyerUserId: string | null;
  amount: number | null;
  paymentMethod: number | null;
  offlineReference: string | null;
  receivedAt: string | null;
  adminNotes: string | null;
}

/** `VoucherReportDto`. */
export interface VoucherReport {
  sold: number;
  salesCollected: number;
  refunded: number;
  salesNet: number;
  complimentaryIssued: number;
  redemptions: number;
  redeemedValue: number;
  outstandingUses: number;
  outstandingValue: number;
  expiredUnusedValue: number;
}
